import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check, Wallet, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtMoney } from "@/lib/api";
import { truncate } from "./themes";

export const PayWalletSection = ({ invoice, address, theme, brand, onPaymentMarked }) => {
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSentForm, setShowSentForm] = useState(false);
  const [txHash, setTxHash] = useState("");
  const hasAddress = !!address;

  const copyAddress = async () => {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      toast.success("Wallet address copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const markSent = async () => {
    setSubmitting(true);
    try {
      await api.post(`/public/invoices/${invoice.invoice_id}/mark-sent`, {
        tx_hash: txHash.trim() || null,
      });
      toast.success("Thanks — the creator has been notified.");
      setShowSentForm(false);
      await onPaymentMarked?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit");
    } finally {
      setSubmitting(false);
    }
  };

  const accentStyle = theme.bgAccent ? { borderColor: brand } : undefined;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.15 }}
      className={`mt-12 p-6 ${theme.payCardCls}`}
      style={accentStyle}
      data-testid="pay-wallet-section"
    >
      <div className={`flex items-center gap-2 ${theme.sectionLabel}`}>
        <Wallet size={12} /> Direct wallet payment
      </div>

      {!hasAddress ? (
        <div className={`mt-6 text-sm ${theme.muted}`} data-testid="no-wallet-warning">
          The creator hasn't set a payment wallet yet. Please reach out to them directly.
        </div>
      ) : (
        <>
          <div className="mt-6 flex flex-col sm:flex-row gap-6 items-center sm:items-start">
            <div className={`shrink-0 ${theme.qrFrame}`} data-testid="wallet-qr">
              <QRCodeSVG value={address} size={148} level="M" />
            </div>
            <div className="flex-1 w-full">
              <div className={`text-xs ${theme.muted} mb-2`}>
                Send <span className={`${theme.body} font-mono`}>{fmtMoney(invoice.total, invoice.currency)}</span> worth of crypto to:
              </div>
              <div className="flex items-stretch gap-2">
                <div
                  className={`flex-1 px-3 py-2.5 font-mono text-xs break-all ${theme.addressCls}`}
                  data-testid="wallet-address"
                  title={address}
                >
                  <span className="hidden sm:inline">{address}</span>
                  <span className="sm:hidden">{truncate(address)}</span>
                </div>
                <button
                  onClick={copyAddress}
                  data-testid="copy-wallet-btn"
                  className={`px-3 transition-colors ${theme.ghostBtn}`}
                  title="Copy"
                >
                  {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                </button>
              </div>
              <div className={`mt-3 text-[11px] ${theme.muted} leading-relaxed`}>
                Send only on the network this address belongs to. Funds sent on the wrong network may be lost.
              </div>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {!showSentForm ? (
              <motion.button
                key="trigger"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowSentForm(true)}
                data-testid="ive-sent-it-btn"
                className={`mt-6 w-full py-3.5 rounded-md font-medium transition-colors inline-flex items-center justify-center gap-2 ${theme.primaryBtn}`}
              >
                <Send size={14} /> I've sent it
              </motion.button>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-6 overflow-hidden"
              >
                <label className="block">
                  <div className={`${theme.sectionLabel} mb-2`}>Transaction hash (optional)</div>
                  <input
                    value={txHash}
                    onChange={(e) => setTxHash(e.target.value)}
                    placeholder="0x… or signature"
                    data-testid="tx-hash-input"
                    className={`w-full px-3 py-2.5 text-sm font-mono focus:outline-none ${theme.inputCls}`}
                  />
                  <div className={`text-[11px] ${theme.muted} mt-2`}>
                    Adding a tx hash lets the creator verify the transfer on-chain.
                  </div>
                </label>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => setShowSentForm(false)}
                    data-testid="cancel-sent-btn"
                    disabled={submitting}
                    className={`flex-1 py-3 rounded-md text-sm transition-colors ${theme.ghostBtn}`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={markSent}
                    disabled={submitting}
                    data-testid="confirm-sent-btn"
                    className={`flex-1 py-3 rounded-md font-medium transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50 ${theme.primaryBtn}`}
                  >
                    {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    Notify creator
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </motion.div>
  );
};
